import React, { useState } from 'react';
import {
  Button,
  ButtonGroup,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCheck,
  faChevronDown,
  faCopy,
  faFileLines,
} from '@crisp/crisp-ui/icons/classic/regular';
import { DEFAULT_SITE, sitePathForSlug, type SiteConfig } from '../../lib/sites';
import styles from './CopyPageButton.module.css';

interface CopyPageButtonProps {
  slug: string;
  site?: SiteConfig;
}

export const CopyPageButton: React.FC<CopyPageButtonProps> = ({ slug, site = DEFAULT_SITE }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const url = `${window.location.origin}${sitePathForSlug(site, slug)}`;
      await navigator.clipboard.writeText(url);
    } finally {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const cleanSlug = slug === 'index' ? 'index' : slug;
  const mdUrl = `/${cleanSlug}.md?site=${site.id}`;

  const copyMarkdownLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${mdUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const openChatGPT = () => {
    const pageUrl = `${window.location.origin}${sitePathForSlug(site, slug)}`;
    window.open(
      `https://chatgpt.com/?q=Read%20this%20documentation%20page%3A%20${encodeURIComponent(pageUrl)}`,
      '_blank'
    );
  };

  const openClaude = () => {
    const pageUrl = `${window.location.origin}${sitePathForSlug(site, slug)}`;
    window.open(
      `https://claude.ai/new?q=Summarize%20this%20documentation%3A%20${encodeURIComponent(pageUrl)}`,
      '_blank'
    );
  };

  return (
    <ButtonGroup className={styles.root}>
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopy}
        aria-label="Copy page"
        iconPrimary={<FontAwesomeIcon icon={copied ? faCheck : faCopy} />}
      >
        {copied ? 'Copied!' : 'Copy page'}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="More actions"
              iconPrimary={<FontAwesomeIcon icon={faChevronDown} />}
            />
          }
        />
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={copyMarkdownLink}>
              <FontAwesomeIcon icon={faFileLines} />
              Copy Markdown Link
            </DropdownMenuItem>
            <DropdownMenuItem render={<a href={mdUrl} target="_blank" rel="noopener noreferrer" />}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              View Raw Markdown
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={openChatGPT}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              Open in ChatGPT
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openClaude}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              Open in Claude
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  );
};
